import { useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendIcon from "@mui/icons-material/Send";

import {
  getAppInsights,
  trackEvent,
  trackException,
  flushTelemetry,
} from "../../services/applicationInsights";

function AppInsightsTest() {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [customEventName, setCustomEventName] = useState("TestEvent");
  const [customProperty, setCustomProperty] = useState("TestValue");

  const addResult = (message: string) => {
    setTestResults((prev) => [...prev, `${new Date().toLocaleTimeString()} - ${message}`]);
  };

  const handleTestCustomEvent = () => {
    trackEvent(customEventName, {
      property: customProperty,
      timestamp: new Date().toISOString(),
      testType: "manual",
    });
    addResult(`✅ Custom event tracked: ${customEventName}`);
    console.log("[Test] Custom event tracked:", customEventName);
    flushTelemetry();
  };

  const handleTestException = () => {
    const testError = new Error("This is a test exception for Application Insights");
    trackException(testError);
    addResult("✅ Exception tracked: Test exception");
    console.log("[Test] Exception tracked");
    flushTelemetry();
  };

  const handleCheckStatus = () => {
    const ai = getAppInsights();
    if (ai) {
      const config = ai.config;
      const connectionStr =
        typeof config.connectionString === "string"
          ? config.connectionString.substring(0, 30)
          : "async";
      addResult(`✅ Application Insights is initialized`);
      addResult(`   Connection String: ${connectionStr}...`);
      addResult(`   Auto Route Tracking: ${config.enableAutoRouteTracking}`);
      addResult(`   CORS Correlation: ${config.enableCorsCorrelation}`);
    } else {
      addResult("❌ Application Insights is NOT initialized");
    }
  };

  const handleClearResults = () => {
    setTestResults([]);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <InfoOutlinedIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Application Insights Test Page
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This page is accessible without authentication to test Application Insights after deployment
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Status Check
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleCheckStatus}
            >
              Check Application Insights Status
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Test Telemetry
          </Typography>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Button variant="contained" onClick={handleTestException}>
              Test Exception Tracking
            </Button>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Custom Event Test
          </Typography>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <TextField
              label="Event Name"
              value={customEventName}
              onChange={(e) => setCustomEventName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Custom Property"
              value={customProperty}
              onChange={(e) => setCustomProperty(e.target.value)}
              fullWidth
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleTestCustomEvent}
              startIcon={<SendIcon />}
            >
              Send Custom Event
            </Button>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Test Results
          </Typography>
          <Box
            sx={{
              bgcolor: "grey.100",
              p: 2,
              borderRadius: 1,
              minHeight: 200,
              maxHeight: 400,
              overflow: "auto",
              fontFamily: "monospace",
              fontSize: "0.875rem",
            }}
          >
            {testResults.length === 0 ? (
              <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
                No tests run yet. Click the buttons above to test Application Insights.
              </Typography>
            ) : (
              testResults.map((result, index) => (
                <Box key={index} sx={{ mb: 0.5 }}>
                  {result}
                </Box>
              ))
            )}
          </Box>
          {testResults.length > 0 && (
            <Button
              variant="text"
              color="error"
              onClick={handleClearResults}
              sx={{ mt: 1 }}
            >
              Clear Results
            </Button>
          )}

          <Box sx={{ mt: 3, p: 2, bgcolor: "info.lighter", borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark">
              <strong>Note:</strong> After running tests, check the Azure Portal Application Insights
              resource to verify that telemetry is being received. It may take a few minutes for data to
              appear.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

export default AppInsightsTest;
